import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import NavBar from "./NavBar";
import Footer from "./Footer";

// Turns any e-mail address into a clickable mailto: link, and any
// `[label](url)` markdown-style link into a real hyperlink:
//   - an internal page (`/some-path`, no file extension) uses React
//     Router's <Link>, e.g. [Privacy Policy](/politica-de-privacidad)
//   - a static file (`/houserules/houserules_es.pdf`) or an external URL
//     opens in a new tab via a plain <a>, same as the footer's PDF links
// This lets translation.json content reference other pages or files (e.g.
// the Privacy Policy or the House Rules PDF from within the Terms of
// Service) without hand-authored markup or a rich-text editor.
const RICH_TEXT_RE =
  /\[([^\]]+)\]\(([^)]+)\)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
const FILE_URL_RE = /\.[a-zA-Z0-9]{2,5}$/; // e.g. .pdf, .jpg — not a SPA route
const isInternalPageLink = (url) => url.startsWith("/") && !FILE_URL_RE.test(url);

const richText = (text) => {
  const str = String(text);
  const nodes = [];
  let lastIndex = 0;
  let match;
  let key = 0;

  RICH_TEXT_RE.lastIndex = 0;
  while ((match = RICH_TEXT_RE.exec(str)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<span key={key++}>{str.slice(lastIndex, match.index)}</span>);
    }

    const [, linkLabel, linkUrl, email] = match;
    if (linkLabel) {
      nodes.push(
        isInternalPageLink(linkUrl) ? (
          <Link key={key++} to={linkUrl} className="text-blue-600 hover:underline">
            {linkLabel}
          </Link>
        ) : (
          <a
            key={key++}
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            {linkLabel}
          </a>
        )
      );
    } else if (email) {
      nodes.push(
        <a key={key++} href={`mailto:${email}`} className="text-blue-600 hover:underline">
          {email}
        </a>
      );
    }

    lastIndex = RICH_TEXT_RE.lastIndex;
  }

  if (lastIndex < str.length) {
    nodes.push(<span key={key++}>{str.slice(lastIndex)}</span>);
  }

  return nodes;
};

/**
 * Generic renderer for long-form legal/compliance pages (privacy policy,
 * terms of service, data-deletion instructions, ...).
 *
 * All copy lives in locales/<lng>/translation.json under `translationKey`,
 * shaped as:
 * {
 *   "title": "...",
 *   "updated": "...",          // optional, shown under the title
 *   "intro": "...",            // optional lead paragraph
 *   "sections": [
 *     {
 *       "heading": "...",       // optional
 *       "blocks": [
 *         { "type": "p", "text": "..." },            // a paragraph
 *         { "type": "list", "items": ["...", "..."] } // a bullet list
 *       ]
 *     }
 *   ]
 * }
 *
 * `blocks` can mix any number of "p" and "list" entries in any order, so a
 * section can open with a paragraph, list some bullet points, then close
 * with another paragraph. See the "How to edit the content" note in each
 * page file for details.
 *
 * Any paragraph or list item text may also contain `[label](/some-path)` to
 * link to another page (e.g. `[Privacy Policy](/politica-de-privacidad)`),
 * or `[label](https://...)` for an external link — see `richText` above.
 */
const LegalPageLayout = ({ translationKey, children }) => {
  const { t } = useTranslation();
  const title = t(`${translationKey}.title`);
  const updated = t(`${translationKey}.updated`, { defaultValue: "" });
  const intro = t(`${translationKey}.intro`, { defaultValue: "" });
  const sections = t(`${translationKey}.sections`, { returnObjects: true, defaultValue: [] });

  return (
    <div className="flex flex-col min-h-screen">
      <NavBar />
      <main className="flex-grow w-full max-w-3xl mx-auto px-4 pt-24 pb-16">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">{title}</h1>
        {updated && <p className="text-sm text-gray-500 mb-6">{updated}</p>}
        {intro && <p className="mb-6 leading-relaxed whitespace-pre-line">{richText(intro)}</p>}

        {Array.isArray(sections) &&
          sections.map((section, idx) => (
            <section key={idx} className="mb-8">
              {section.heading && (
                <h2 className="text-lg md:text-xl font-semibold mb-3">{section.heading}</h2>
              )}
              {Array.isArray(section.blocks) &&
                section.blocks.map((block, bIdx) => {
                  if (block.type === "list" && Array.isArray(block.items)) {
                    return (
                      <ul key={bIdx} className="list-disc pl-6 space-y-2 mb-3">
                        {block.items.map((item, lIdx) => (
                          <li key={lIdx} className="leading-relaxed">
                            {richText(item)}
                          </li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p key={bIdx} className="mb-3 leading-relaxed whitespace-pre-line">
                      {richText(block.text)}
                    </p>
                  );
                })}
            </section>
          ))}

        {children}
      </main>
      <Footer />
    </div>
  );
};

export default LegalPageLayout;
